import { router, type Href } from "expo-router";

/**
 * A route push transition takes roughly this long to complete. Repeat taps
 * during that window are the cause of DEL-380 (spam-clicking a camera or nav
 * item stacks the same screen multiple times before the transition finishes).
 */
const PUSH_GUARD_MS = 800;

let guardTimeoutId: ReturnType<typeof setTimeout> | undefined;
let pendingHref: Href | undefined;

function clearGuard() {
  clearTimeout(guardTimeoutId);
  guardTimeoutId = undefined;
  pendingHref = undefined;
}

function armGuard(href: Href) {
  clearTimeout(guardTimeoutId);
  pendingHref = href;
  guardTimeoutId = setTimeout(clearGuard, PUSH_GUARD_MS);
}

/**
 * Pushes `href` while a previous push is still settling.
 *
 * - A repeat tap on the same href is a duplicate and is ignored.
 * - A tap on a different href is a correction (the user changed their
 *   mind) — it replaces the pending screen instead of stacking on top of
 *   it, so the most recent choice wins without growing the stack.
 */
export function guardedPush(href: Href) {
  const isCorrection = guardTimeoutId !== undefined;

  if (isCorrection && JSON.stringify(href) === JSON.stringify(pendingHref)) return;

  armGuard(href);

  try {
    if (isCorrection) {
      router.replace(href);
    } else {
      router.push(href);
    }
  } catch (error) {
    clearGuard();
    throw error;
  }
}
